import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { Photo } from "../../svg";
import ImagePreview from "../createPostPopup/ImagePreview";
import { uploadImages } from "../../functions/uploadImages";
import { createPost } from "../../functions/post";
import dataURItoBlob from "../../helpers/dataURItoBlob";
import PulseLoader from "react-spinners/PulseLoader";

// Helper function to get icon based on title/life event name
const getIconForTitle = (title) => {
  if (!title || !title.trim()) return "🚩";
  
  // Remove any existing icon from the title
  const titleWithoutIcon = title.replace(/^[\u{1F300}-\u{1F9FF}]+\s*/u, '').trim();
  const titleToCheck = titleWithoutIcon || title.trim();
  
  // Map of specific titles to their icons
  const titleToIconMap = {
    // Work
    "New Job": "🆕💼",
    "Promotion": "📈",
    "Left Job": "📤",
    "Retirement": "🎉",
    "Work Anniversary": "🎂",
    "Started New Project": "🚀",
    
    // Education
    "Graduated": "🎓",
    "Started School": "🏫",
    "Got a Degree": "📜",
    "Finished Course": "📚",
    "Achieved Certification": "🏅",
    "Enrolled in Program": "📝",
    
    // Relationship
   "Started Relationship": "❤️",
   "In a Relationship": "💑",
   "Got Engaged": "💍",
   "Got Married": "👰🤵",
   "Anniversary": "💕",
   "It's Complicated": "😕",
   "Single": "🙂",
   "Divorced": "💔",
    
    // Home
    "Moved": "🏠",
    "Bought a Home": "🏡",
    "Sold a Home": "🏠",
    "Renovated": "🔨",
    "New Roommate": "👥",
    "New Pet": "🐾",
    
    // Family
    "New Family Member": "👶",
    "Family Member's Birthday": "🎂",
    "Family Reunion": "👨‍👩‍👧‍👦",
    "Family Milestone": "👨‍👩‍👧‍👦",
    "Family Celebration": "🎉",
    
    // Travel
    "New City": "🌆",
    "New Country": "🌍",
    "Vacation": "✈️",
    "Backpacking": "🎒",
    "Road Trip": "🚗",
    "Adventure": "🏔️",
    
    // Interests
    "New Hobby": "⭐",
    "Achievement": "🏆",
    "Competition": "🥇",
    "Event": "🎪",
    "New Skill": "🎯",
    "Milestone": "🎖️",
    
    // Health
    "Fitness Goal": "💪",
    "Health Milestone": "💚",
    "Wellness Achievement": "🧘",
    "Recovery": "🏥",
    "Health Update": "💊",
    
    // Milestones
    "Personal Achievement": "🏆",
    "Career Milestone": "📈",
    "Life Milestone": "🌟",
    "Award": "🏅",
    "Recognition": "👏",
    "Accomplishment": "✅",
    
    // Remembrance
    "Memorial": "🕯️",
    "Tribute": "🌹",
    "In Memory": "🖤",
    "Remembrance": "🎗️"
  };
  
  // Check exact match first
  if (titleToIconMap[titleToCheck]) {
    return titleToIconMap[titleToCheck];
  }
  
  // Check case-insensitive match
  const lowerTitle = titleToCheck.toLowerCase();
  for (const [key, icon] of Object.entries(titleToIconMap)) {
    if (key.toLowerCase() === lowerTitle) {
      return icon;
    }
  }
  
  // Fallback: determine by category if title matches known events
  const lifeEventsByCategory = {
    work: ["New Job", "Promotion", "Left Job", "Retirement", "Work Anniversary", "Started New Project"],
    education: ["Graduated", "Started School", "Got a Degree", "Finished Course", "Achieved Certification", "Enrolled in Program"],
    relationship: ["Started Relationship", "Got Engaged", "Got Married", "In a Relationship", "It's Complicated", "Single", "Anniversary", "Divorced"],
    home: ["Moved", "Bought a Home", "Sold a Home", "Renovated", "New Roommate", "New Pet"],
    family: ["New Family Member", "Family Member's Birthday", "Family Reunion", "Family Milestone", "Family Celebration"],
    travel: ["New City", "New Country", "Vacation", "Backpacking", "Road Trip", "Adventure"],
    interests: ["New Hobby", "Achievement", "Competition", "Event", "New Skill", "Milestone"],
    health: ["Fitness Goal", "Health Milestone", "Wellness Achievement", "Recovery", "Health Update"],
    milestones: ["Personal Achievement", "Career Milestone", "Life Milestone", "Award", "Recognition", "Accomplishment"],
    remembrance: ["Memorial", "Tribute", "In Memory", "Remembrance"],
  };
  
  const categoryIcons = {
    work: "💼",
    education: "🎓",
    relationship: "❤️",
    home: "🏠",
    family: "👨‍👩‍👧‍👦",
    travel: "🌍",
    interests: "⭐",
    health: "💚",
    milestones: "🏆",
    remembrance: "🕯️",
    custom: "🚩",
  };
  
  // Check if title matches any known event
  for (const [category, events] of Object.entries(lifeEventsByCategory)) {
    if (events.some(event => event.toLowerCase() === lowerTitle)) {
      return categoryIcons[category];
    }
  }
  
  // Default icon for custom/unknown titles
  return categoryIcons.custom;
};

// Field configurations for different life event types
const getFieldsForLifeEvent = (categoryId, lifeEvent) => {
  const fieldConfigs = {
    work: {
      "New Job": [
        { id: "workplace", label: "Workplace", type: "text", placeholder: "Company name", required: true },
        { id: "position", label: "Position", type: "text", placeholder: "Job title", required: false },
        { id: "startDate", label: "Start Date", type: "date", placeholder: "When did you start?", required: false },
      ],
      "Promotion": [
        { id: "workplace", label: "Workplace", type: "text", placeholder: "Company name", required: true },
        { id: "newPosition", label: "New Position", type: "text", placeholder: "New job title", required: true },
        { id: "date", label: "Date", type: "date", placeholder: "When was your promotion?", required: false },
      ],
      "Left Job": [
        { id: "workplace", label: "Workplace", type: "text", placeholder: "Company name", required: false },
        { id: "endDate", label: "End Date", type: "date", placeholder: "When did you leave?", required: false },
      ],
      "Retirement": [
        { id: "workplace", label: "Workplace", type: "text", placeholder: "Company name", required: false },
        { id: "retirementDate", label: "Retirement Date", type: "date", placeholder: "When did you retire?", required: false },
      ],
      default: [
        { id: "workplace", label: "Workplace", type: "text", placeholder: "Company name", required: false },
        { id: "date", label: "Date", type: "date", placeholder: "When did this happen?", required: false },
      ],
    },
    education: {
      "Graduated": [
        { id: "school", label: "School", type: "text", placeholder: "School or university name", required: true },
        { id: "degree", label: "Degree", type: "text", placeholder: "Degree or major", required: false },
        { id: "graduationDate", label: "Graduation Date", type: "date", placeholder: "When did you graduate?", required: false },
      ],
      default: [
        { id: "school", label: "School", type: "text", placeholder: "School or university name", required: false },
        { id: "date", label: "Date", type: "date", placeholder: "When did this happen?", required: false },
      ],
    },
    relationship: {
      default: [
        { id: "date", label: "Date", type: "date", placeholder: "When did this happen?", required: false },
      ],
    },
    home: {
      "Moved": [
        { id: "location", label: "Location", type: "text", placeholder: "City or address", required: true },
        { id: "moveDate", label: "Move Date", type: "date", placeholder: "When did you move?", required: false },
      ],
      "Bought a Home": [
        { id: "location", label: "Location", type: "text", placeholder: "City or address", required: true },
        { id: "purchaseDate", label: "Purchase Date", type: "date", placeholder: "When did you buy it?", required: false },
      ],
      default: [
        { id: "location", label: "Location", type: "text", placeholder: "City or address", required: false },
        { id: "date", label: "Date", type: "date", placeholder: "When did this happen?", required: false },
      ],
    },
    family: {
      default: [
        { id: "date", label: "Date", type: "date", placeholder: "When did this happen?", required: false },
        { id: "description", label: "Description", type: "textarea", placeholder: "Tell us more about this event", required: false },
      ],
    },
    travel: {
      default: [
        { id: "destination", label: "Destination", type: "text", placeholder: "Where did you go?", required: false },
        { id: "date", label: "Date", type: "date", placeholder: "When did you travel?", required: false },
      ],
    },
    default: {
      default: [
        { id: "date", label: "Date", type: "date", placeholder: "When did this happen?", required: false },
        { id: "description", label: "Description", type: "textarea", placeholder: "Tell us more about this event", required: false },
      ],
    },
  };

  const categoryConfig = fieldConfigs[categoryId] || fieldConfigs.default;
  return categoryConfig[lifeEvent] || categoryConfig.default || [];
};

export default function CreateLifeUpdateForm({ 
  user, 
  lifeEventData, 
  onClose,
  onBack,
  dispatch, 
  getAllPosts 
}) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [showPrev, setShowPrev] = useState(false);
  const [formFields, setFormFields] = useState({});
  const mediaInputRef = useRef(null);
  const popupRef = useRef(null);
  const [background, setBackground] = useState("");
  const [showThemes, setShowThemes] = useState(false);

  // Initialize title and form fields based on life event data
  useEffect(() => {
    if (lifeEventData) {
      const { category, lifeEvent } = lifeEventData;
      
      // Set the title based on the life event (only the event name, no emoji)
      setTitle(lifeEvent);

      // Get and initialize form fields
      const fields = getFieldsForLifeEvent(category.id, lifeEvent);
      const initialFields = {};
      // Get today's date in YYYY-MM-DD format for date fields
      const today = new Date().toISOString().split('T')[0];
      fields.forEach(field => {
        // Set default value to today's date for date fields, empty string for others
        initialFields[field.id] = field.type === "date" ? today : "";
      });
      setFormFields(initialFields);
    }
  }, [lifeEventData]);

  const handleFieldChange = (fieldId, value) => {
    setFormFields(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleMedia = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");

      if (isImage && file.size > 5 * 1024 * 1024) {
        setError(`${file.name} size is too large! Max 5 MB allowed for images.`);
        return;
      }
      if (isVideo && file.size > 1024 * 1024 * 1024) {
        setError(`${file.name} size is too large! Max 1 GB allowed for videos.`);
        return;
      }

      if (!isImage && !isVideo) {
        setError(`${file.name} format is unsupported!`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (readerEvent) => {
        if (isImage) {
          setImages(prev => [...prev, readerEvent.target.result]);
          setShowPrev(true);
          // Clear background when images are added
          setBackground("");
        } else if (isVideo) {
          setVideos(prev => [...prev, readerEvent.target.result]);
          setShowPrev(true);
          // Clear background when videos are added
          setBackground("");
        }
      };
      reader.readAsDataURL(file);
    });
    if (mediaInputRef.current) {
      mediaInputRef.current.value = "";
    }
  };

  const buildPostText = () => {
    // Determine icon based on the title value, not the category
    const iconForTitle = getIconForTitle(title);
    // Include the title-based icon with the title in the post text
    return `${iconForTitle} ${title.trim()}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    try {
      setLoading(true);
      const postText = buildPostText();

      if (images && images.length > 0) {
        const postImages = images.map((img) => dataURItoBlob(img));
        const path = `${user.username}/post Images`;
        let formData = new FormData();
        formData.append("path", path);
        postImages.forEach((img) => {
          formData.append("file", img);
        });

        const imageResponse = await uploadImages(formData, path, user.token);
        const res = await createPost(
          null,
          background || null,
          postText,
          imageResponse,
          user.id,
          user.token,
          null,
          null,
          null,
          []
        );

        setLoading(false);
        if (res.status === "ok") {
          if (dispatch) {
            dispatch({ type: "ADD_POST", payload: res.data });
          }
          if (getAllPosts) {
            getAllPosts();
          }
          onClose();
          navigate("/");
        } else {
          setError(res.message || "Failed to create life update");
        }
      } else if (videos && videos.length > 0) {
        const postVideos = videos.map((video, idx) => dataURItoBlob(video, `post_video_${Date.now()}_${idx}`));
        const path = `${user.username}/post Videos`;
        let formData = new FormData();
        formData.append("path", path);
        postVideos.forEach((video) => {
          formData.append("file", video);
        });

        const videoResponse = await uploadImages(formData, path, user.token);
        const res = await createPost(
          null,
          background || null,
          postText,
          videoResponse,
          user.id,
          user.token,
          null,
          null,
          null,
          []
        );

        setLoading(false);
        if (res.status === "ok") {
          if (dispatch) {
            dispatch({ type: "ADD_POST", payload: res.data });
          }
          if (getAllPosts) {
            getAllPosts();
          }
          onClose();
          navigate("/");
        } else {
          setError(res.message || "Failed to create life update");
        }
      } else {
        // Text-only post (with or without background)
        const res = await createPost(
          null,
          background || null,
          postText,
          null,
          user.id,
          user.token,
          null,
          null,
          null,
          []
        );

        setLoading(false);
        if (res.status === "ok") {
          if (dispatch) {
            dispatch({ type: "ADD_POST", payload: res.data });
          }
          if (getAllPosts) {
            getAllPosts();
          }
          onClose();
          navigate("/");
        } else {
          setError(res.message || "Failed to create life update");
        }
      }
    } catch (err) {
      setLoading(false);
      setError(err.message || "Failed to create life update");
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    onClose();
  };

  if (!lifeEventData) {
    return null;
  }

  const { category, lifeEvent } = lifeEventData;
  const fields = getFieldsForLifeEvent(category.id, lifeEvent);

  return createPortal(
    <div className="create_life_update_overlay" onClick={onClose}>
      <div className="create_life_update_form" onClick={(e) => e.stopPropagation()} ref={popupRef}>
        {/* Header */}
        <div className="create_life_update_form_header">
          <button onClick={handleBack} className="back_btn" aria-label="Back to event selection" type="button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h2>Create Life Update</h2>
          <div style={{ width: "24px" }}></div>
        </div>

        {/* Form Content */}
        <form className="create_life_update_form_content" onSubmit={handleSubmit}>
          {/* Title */}
          <div className="form_group">
            <label htmlFor="title" className="form_label">
              Title <span className="required">*</span>
            </label>
            <input
              type="text"
              id="title"
              className="form_input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Life update title"
              required
            />
          </div>

          {/* Dynamic Fields */}
          {fields.map((field) => (
            <div key={field.id} className="form_group">
              <label htmlFor={field.id} className="form_label">
                {field.label}
                {field.required && <span className="required">*</span>}
              </label>
              {field.type === "textarea" ? (
                <textarea
                  id={field.id}
                  className="form_textarea"
                  value={formFields[field.id] || ""}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  placeholder={field.placeholder}
                  rows={3}
                  required={field.required}
                />
              ) : (
                <input
                  type={field.type}
                  id={field.id}
                  className="form_input"
                  value={formFields[field.id] || ""}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  placeholder={field.placeholder}
                  required={field.required}
                />
              )}
            </div>
          ))}

          {/* Additional Text */}
          <div className="form_group">
            <label htmlFor="text" className="form_label">
              Additional Details
            </label>
            <textarea
              id="text"
              className="form_textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Share more about this life event..."
              rows={4}
            />
          </div>

          {/* Theme Selection - Only for custom events */}
          {lifeEventData && lifeEventData.isCustom && (
            <div className="form_group">
              <label className="form_label">Choose Theme or Add Images</label>
              <div className="theme_selection_wrapper">
                {/* Theme Preview */}
                {background && (
                  <div 
                    className="theme_preview"
                    style={{ backgroundImage: `url(${background})` }}
                  >
                    <button
                      type="button"
                      className="remove_theme_btn"
                      onClick={() => {
                        setBackground("");
                        setShowThemes(false);
                      }}
                    >
                      <i className="exit_icon"></i>
                    </button>
                  </div>
                )}
                
                {/* Theme Selector */}
                <div className="theme_selector_controls">
                  <div className="theme_options">
                    <button
                      type="button"
                      className={`theme_option_btn ${!background ? 'no_bg' : ''}`}
                      onClick={() => {
                        setBackground("");
                        setShowThemes(!showThemes);
                      }}
                      title="Remove theme"
                    >
                      <span className="theme_option_x">×</span>
                    </button>
                    <button
                      type="button"
                      className="theme_option_btn theme_landscape_btn"
                      onClick={() => setShowThemes(!showThemes)}
                      title="Select theme"
                    >
                      🏔️
                    </button>
                    {[
                      "../../../images/postbackgrounds/1.jpg",
                      "../../../images/postbackgrounds/2.jpg",
                      "../../../images/postbackgrounds/3.jpg",
                      "../../../images/postbackgrounds/4.jpg",
                      "../../../images/postbackgrounds/5.jpg",
                    ].map((bg, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`theme_option_btn ${background === bg ? 'selected' : ''}`}
                        onClick={() => {
                          setBackground(bg);
                          setShowThemes(false);
                          // Clear images when theme is selected
                          setImages([]);
                          setVideos([]);
                          setShowPrev(false);
                        }}
                        style={{ backgroundImage: `url(${bg})` }}
                        title={`Theme ${i + 1}`}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    className="photos_videos_btn"
                    onClick={() => {
                      setBackground("");
                      mediaInputRef.current?.click();
                    }}
                  >
                    <Photo color="#fff" />
                    <span>Photos/videos</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Media Upload */}
          <div className="form_group">
            <label className="form_label">Add Photos/Videos</label>
            <button
              type="button"
              className="media_upload_btn"
              onClick={() => mediaInputRef.current?.click()}
            >
              <Photo color="#4bbf67" />
              <span>Add Photos/Videos</span>
            </button>
            <input
              ref={mediaInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleMedia}
              style={{ display: "none" }}
            />
          </div>

          {/* Media Preview */}
          {showPrev && (images.length > 0 || videos.length > 0) && (
            <ImagePreview
              text={text}
              user={user}
              setText={setText}
              images={images}
              videos={videos}
              setImages={setImages}
              setVideos={setVideos}
              setShowPrev={setShowPrev}
              setError={setError}
              hideTextInput={true}
            />
          )}

          {/* Error Message */}
          {error && (
            <div className="form_error">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="form_actions">
            <button
              type="button"
              className="form_cancel_btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="form_submit_btn"
              disabled={loading || !title.trim()}
            >
              {loading ? (
                <PulseLoader color="#fff" size={8} />
              ) : (
                "Post Life Update"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
